import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarDays } from 'lucide-react';
import { useLang } from '../context/LangContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HI_DAYS = { Monday: 'सोमवार', Tuesday: 'मंगलवार', Thursday: 'गुरुवार', Friday: 'शुक्रवार' };

export const slotLabel = (date, time) => new Date(`${date}T${time}:00+05:30`).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
const localTime = (date, time) => new Date(`${date}T${time}:00+05:30`).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
const isIST = () => Intl.DateTimeFormat().resolvedOptions().timeZone === 'Asia/Kolkata' || new Date().getTimezoneOffset() === -330;

export const SlotPicker = ({ value, onChange, showLocal }) => {
  const { tr } = useLang();
  const [days, setDays] = useState(null);
  const [day, setDay] = useState(null);

  useEffect(() => {
    axios.get(`${API}/slots`).then(({ data }) => {
      setDays(data);
      setDay(data.find((d) => d.slots.some((s) => s.available))?.date || data[0]?.date);
    }).catch(() => setDays([]));
  }, []);

  if (!days) return <p className="text-sm text-slate-400">{tr('Loading available times…', 'उपलब्ध समय लोड हो रहे हैं…')}</p>;
  const current = days.find((d) => d.date === day);
  const local = showLocal && !isIST();

  return (
    <div data-testid="slot-picker">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><CalendarDays size={16} className="text-teal-600" /> {tr('Choose a date and time (India time, IST)', 'तारीख़ और समय चुनें (भारतीय समय, IST)')}</div>
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
        {days.map((d) => {
          const full = !d.slots.some((s) => s.available);
          const dt = new Date(`${d.date}T00:00:00+05:30`);
          return (
            <button key={d.date} type="button" data-testid={`slot-day-${d.date}`} disabled={full} onClick={() => { setDay(d.date); onChange(null); }}
              className={`shrink-0 w-[72px] rounded-xl border px-2 py-2 text-center transition-colors ${day === d.date ? 'bg-teal-700 text-white border-teal-700' : full ? 'border-slate-100 text-slate-300 cursor-not-allowed' : 'border-teal-100 text-teal-900 hover:border-teal-500'}`}>
              <div className="text-[11px] uppercase">{tr(d.weekday.slice(0, 3), HI_DAYS[d.weekday])}</div>
              <div className="text-lg font-semibold leading-tight">{dt.toLocaleDateString('en-GB', { day: 'numeric', timeZone: 'Asia/Kolkata' })}</div>
              <div className="text-[11px]">{full ? tr('Full', 'भरा') : dt.toLocaleDateString('en-GB', { month: 'short', timeZone: 'Asia/Kolkata' })}</div>
            </button>
          );
        })}
      </div>
      {current && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
          {current.slots.map((s) => {
            const sel = value?.date === current.date && value?.time === s.time;
            return (
              <button key={s.time} type="button" data-testid={`slot-time-${current.date}-${s.time.replace(':', '')}`} disabled={!s.available} onClick={() => onChange({ date: current.date, time: s.time })}
                className={`rounded-xl border py-2.5 text-sm font-semibold transition-colors ${sel ? 'bg-coral text-white border-transparent' : s.available ? 'border-teal-100 text-teal-800 hover:border-teal-500 hover:bg-teal-50' : 'border-slate-100 text-slate-300 line-through cursor-not-allowed'}`}
                style={sel ? { background: 'var(--coral)' } : undefined}>
                {s.time}
                {local && <span className={`block text-[10px] font-normal ${sel ? 'text-white/90' : 'text-slate-500'}`}>{localTime(current.date, s.time)} {tr('your time', 'आपका समय')}</span>}
              </button>
            );
          })}
        </div>
      )}
      {days.length === 0 && <p className="text-sm text-slate-500">{tr('No slots available right now. Please book on WhatsApp.', 'अभी कोई समय उपलब्ध नहीं है। कृपया व्हाट्सऐप पर बुक करें।')}</p>}
    </div>
  );
};
